import { useId, type ReactNode } from 'react';
import { Box, type BoxProps } from './box';
import { useScreenFit } from './FitStage';
import {
  TABLE_BEVEL,
  TABLE_FELT_EDGE,
  TABLE_STITCHING,
  TableGradients,
} from './tablePaint';

// The walnut-rim felt oval (Figma `Table surface` 393:163, as drawn on Home
// 1659:20364). The Figma file builds it from ~130 vector layers, but nearly
// all of those are walnut-grain slivers at ~4% opacity and hairline felt nap.
// What the eye sees is a few gradient ellipses, so this draws just those as
// one inline SVG: 9 shapes, no images, no blur filters, no animation. Stroke
// widths and the stitching dashes are non-scaling, so the art stretches to
// the screen width without distorting its lines. The felt spade is a symbol,
// so it doesn't stretch: it has its own SVG over the same frame, scaled
// uniformly to fit the table and centred.
//
// Geometry and colours are read from the Figma layers (390 x 404 frame).
const SPADE_PATH =
  'M10 0C7.91667 4.125 1.25 8.25 1.25 13.2917C1.25 18.3333 7.08333 19.7083 9.58333 15.5833C9.16667 18.7917 7.5 20.625 5.83333 21.5417H14.1667C12.5 20.625 10.8333 18.7917 10.4167 15.5833C12.9167 19.7083 18.75 18.3333 18.75 13.2917C18.75 8.25 12.0833 4.125 10 0Z';

const stroke = { vectorEffect: 'non-scaling-stroke' } as const;

const artStyle = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  pointerEvents: 'none',
} as const;

interface TableSurfaceProps extends BoxProps {
  children?: ReactNode;
  /** The faint felt spade. The short history table (`Table surface · short`
   *  1089:4224) has none: "TAP A NUMBER TO EDIT" is printed there instead. */
  showSpade?: boolean;
}

function TableSurface({
  children,
  showSpade = true,
  ...rest
}: TableSurfaceProps) {
  // Gradient ids must be unique per instance (the dealer modal can show a
  // second table), and useId's colons aren't safe inside url(#...).
  const id = useId().replace(/:/g, '');
  const ref = (name: string) => `url(#${id}-${name})`;
  // The fit rule's table/oval-height (Full 404 to Short 360); the art
  // stretches to it. Other tables pass their own height.
  const { tableHeight } = useScreenFit();

  return (
    <Box
      data-testid="table-surface"
      position="relative"
      width="100%"
      height={`${tableHeight}px`}
      {...rest}
    >
      <svg
        data-testid="table-art"
        aria-hidden="true"
        viewBox="0 0 390 404"
        preserveAspectRatio="none"
        style={artStyle}
      >
        <defs>
          <TableGradients id={id} />
          <linearGradient id={`${id}-shade`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#000" stopOpacity="0.08" />
            <stop offset="0.18" stopColor="#000" stopOpacity="0.12" />
            <stop offset="0.34" stopColor="#000" stopOpacity="0" />
            <stop offset="0.67" stopColor="#000" stopOpacity="0.05" />
            <stop offset="1" stopColor="#000" stopOpacity="0.3" />
          </linearGradient>
          {/* Soft uneven light and shade (Figma "Felt depth"): two blobs,
              drawn as feathered radial gradients instead of a blur filter. */}
          <radialGradient id={`${id}-light`}>
            {/* Figma's mint glow colour (mint-glow-a8) at 12%. */}
            <stop offset="0" stopColor="#9cdec0" stopOpacity="0.12" />
            <stop offset="1" stopColor="#9cdec0" stopOpacity="0" />
          </radialGradient>
          <radialGradient id={`${id}-dark`}>
            <stop
              offset="0"
              stopColor="var(--spades-felt-deep)"
              stopOpacity="0.2"
            />
            <stop
              offset="1"
              stopColor="var(--spades-felt-deep)"
              stopOpacity="0"
            />
          </radialGradient>
        </defs>

        {/* Walnut outer edge, bevel and inner band */}
        <ellipse
          data-part="walnut"
          cx="195"
          cy="202"
          rx="185.4"
          ry="202"
          fill={ref('walnut')}
        />
        <ellipse
          data-part="bevel"
          cx="195"
          cy="202"
          rx="184.49"
          ry="201.09"
          fill="none"
          stroke={TABLE_BEVEL}
          strokeWidth="0.91"
          {...stroke}
        />
        <ellipse
          data-part="band"
          cx="195"
          cy="202"
          rx="182.2"
          ry="198.8"
          fill={ref('band')}
        />

        {/* Felt, its top-to-bottom shading and the two soft blobs */}
        <ellipse
          data-part="felt"
          cx="195"
          cy="201.77"
          rx="175.34"
          ry="191.26"
          fill={ref('felt')}
        />
        <ellipse
          cx="195"
          cy="201.77"
          rx="175.34"
          ry="191.26"
          fill={ref('shade')}
        />
        <ellipse cx="110" cy="150" rx="82" ry="66" fill={ref('light')} />
        <ellipse cx="248" cy="272" rx="102" ry="82" fill={ref('dark')} />

        {/* Felt inset edge, then the stitching just inside it */}
        <ellipse
          data-part="inset-edge"
          cx="195"
          cy="201.77"
          rx="175.8"
          ry="191.72"
          fill="none"
          stroke={TABLE_FELT_EDGE}
          strokeWidth="1.37"
          {...stroke}
        />
        <ellipse
          data-part="stitching"
          cx="195"
          cy="201.55"
          rx="168.94"
          ry="184.18"
          fill="none"
          stroke={TABLE_STITCHING}
          strokeWidth="0.73"
          strokeDasharray="5.03 5.03"
          {...stroke}
        />
      </svg>

      {/* The faint spade in the middle of the felt, keeping its Figma shape
          however the table stretches: "meet" scales the frame by the smaller
          of the table's two scales. */}
      {showSpade && (
        <svg
          data-testid="table-spade"
          aria-hidden="true"
          viewBox="0 0 390 404"
          preserveAspectRatio="xMidYMid meet"
          style={artStyle}
        >
          <path
            data-part="spade"
            d={SPADE_PATH}
            fill="var(--spades-ivory)"
            fillOpacity="0.07"
            transform="translate(128.48 123.9) scale(6.63 7.045)"
          />
        </svg>
      )}
      {children}
    </Box>
  );
}

export default TableSurface;
